import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery } from "@tanstack/react-query";
import React from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { toast } from "@/components/ui/use-toast";
import { useConfirmDialog } from "@/context";
import { useI18n } from "@/context/i18n";
import { queryClient } from "@/lib/api";

import * as api from "./payment-gateways.api";

const formSchema = z.object({
  slug: z
    .string()
    .min(1)
    .regex(/^[a-z0-9_]+$/, { message: "Slug inválido" }),
  name: z.string().min(1),
  description: z.string().optional().nullable(),
  is_active: z.boolean(),
});

export type TPaymentGatewayFormData = z.infer<typeof formSchema>;

export function usePaymentGatewaysAdmin() {
  const { t } = useI18n();
  const { openDialog } = useConfirmDialog();
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [editingId, setEditingId] = React.useState<string | null>(null);

  const { data: gateways, isLoading } = useQuery({
    queryKey: ["payment-gateways", "admin"],
    queryFn: () => api.getPaymentGateways(true),
  });

  const form = useForm<TPaymentGatewayFormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      slug: "",
      name: "",
      description: "",
      is_active: true,
    },
  });

  const createMutation = useMutation({
    mutationFn: api.createPaymentGateway,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["payment-gateways"] });
      toast({ title: t("generic.success"), description: t("paymentGateways.success.created") });
      closeDialog();
    },
    onError: (e: Error) => {
      toast({
        title: t("generic.error"),
        description: e.message,
        variant: "destructive",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<TPaymentGatewayFormData> }) =>
      api.updatePaymentGateway(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["payment-gateways"] });
      toast({ title: t("generic.success"), description: t("paymentGateways.success.updated") });
      closeDialog();
    },
    onError: (e: Error) => {
      toast({
        title: t("generic.error"),
        description: e.message,
        variant: "destructive",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: api.deletePaymentGateway,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["payment-gateways"] });
      toast({ title: t("generic.success"), description: t("paymentGateways.success.deleted") });
    },
    onError: (e: Error) => {
      toast({
        title: t("generic.error"),
        description: e.message,
        variant: "destructive",
      });
    },
  });

  const openCreate = () => {
    setEditingId(null);
    form.reset({
      slug: "",
      name: "",
      description: "",
      is_active: true,
    });
    setDialogOpen(true);
  };

  const openEdit = (id: string) => {
    const g = gateways?.find((x) => x.id === id);
    if (!g) return;
    setEditingId(id);
    form.reset({
      slug: g.slug,
      name: g.name,
      description: g.description ?? "",
      is_active: g.is_active,
    });
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingId(null);
  };

  const onSubmit = form.handleSubmit((data) => {
    const payload = {
      ...data,
      description: data.description?.trim() ? data.description : null,
    };
    if (editingId) {
      updateMutation.mutate({ id: editingId, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  });

  const confirmDelete = (id: string, name: string) => {
    openDialog(
      t("paymentGateways.delete.title"),
      `${t("paymentGateways.delete.description")} (${name})`,
      () => deleteMutation.mutate(id),
    );
  };

  return {
    gateways,
    isLoading,
    dialogOpen,
    setDialogOpen,
    editingId,
    form,
    openCreate,
    openEdit,
    closeDialog,
    onSubmit,
    confirmDelete,
    isSaving: createMutation.isPending || updateMutation.isPending,
  };
}
